import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { MergeChoice, MergeResolution, SaveVersionConflict } from "../documentAuthoring.types";
import { toResolutions } from "./helpers";

interface Props {
  conflict: SaveVersionConflict | null;
  submitting?: boolean;
  onCancel: () => void;
  onResubmit: (resolutions: MergeResolution[]) => void;
}

const CHOICES: Array<{ value: MergeChoice; label: string }> = [
  { value: "mine", label: "Keep mine" },
  { value: "theirs", label: "Keep theirs" },
  { value: "both", label: "Keep both" },
];

const Column: React.FC<{ title: string; text: string; tone?: "base" | "mine" | "theirs" }> = ({
  title,
  text,
  tone = "base",
}) => (
  <div
    className={cn(
      "min-w-0 rounded-md border p-2",
      tone === "base" && "border-desert-sand-300 bg-ivory-mist-100",
      tone === "mine" && "border-blue-200 bg-blue-50",
      tone === "theirs" && "border-amber-200 bg-amber-50",
    )}
  >
    <p className="mb-1 text-xxs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
    <p className="whitespace-pre-wrap break-words text-xs text-ui-paragraph-text">{text || <em>(empty)</em>}</p>
  </div>
);

/**
 * 409 on save (D6): somebody saved a newer version and the three-way merge could not decide
 * these blocks. Shows base / mine / theirs per block and resubmits with `resolutions`.
 */
export const MergeConflictDialog: React.FC<Props> = ({ conflict, submitting, onCancel, onResubmit }) => {
  const [choices, setChoices] = React.useState<Record<string, MergeChoice>>({});

  React.useEffect(() => {
    setChoices({});
  }, [conflict]);

  if (!conflict) return null;
  const decided = conflict.conflicts.every(block => choices[block.block_id] !== undefined);

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto" closeDisabled={submitting}>
        <DialogHeader>
          <DialogTitle>Resolve conflicting changes</DialogTitle>
          <DialogDescription>
            {conflict.head.created_by_name} saved version {conflict.head.version_no} while you were editing.{" "}
            {conflict.detail}
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-4">
          {conflict.conflicts.map((block, index) => (
            <li key={block.block_id} className="space-y-2 rounded-md border border-desert-sand-300 p-3">
              <p className="text-xs font-semibold text-ui-heading-text">
                Block {index + 1}
                {block.part && block.part !== "/word/document.xml" && (
                  <span className="ml-1 font-normal text-gray-500">({block.part})</span>
                )}
              </p>
              <div className="grid gap-2 md:grid-cols-3">
                <Column title="Base" text={block.base_text} />
                <Column title="Mine" text={block.mine_text} tone="mine" />
                <Column title={`Theirs (${block.theirs_author})`} text={block.theirs_text} tone="theirs" />
              </div>
              <div role="radiogroup" aria-label={`Resolution for block ${index + 1}`} className="flex flex-wrap gap-3">
                {CHOICES.map(choice => (
                  <label key={choice.value} className="flex cursor-pointer items-center gap-1.5 text-xs">
                    <input
                      type="radio"
                      name={`resolution-${block.block_id}`}
                      value={choice.value}
                      checked={choices[block.block_id] === choice.value}
                      onChange={() => setChoices(prev => ({ ...prev, [block.block_id]: choice.value }))}
                      disabled={submitting}
                    />
                    {choice.label}
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ol>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onResubmit(toResolutions(conflict.conflicts, choices))}
            disabled={!decided || submitting}
          >
            {submitting ? "Saving…" : "Save merged version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
